import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useFormContext, Controller } from 'react-hook-form';

import { intlMessage } from 'constants/propTypes';
import { bem } from 'helpers/class-name';
import { DateControls } from 'components/DateControls';
import { zonedTimeToUtc, adjustDateForTimezone } from 'helpers/intl-fns';

import Label from '../Label/Label';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import { makeValidators } from '../helpers';

const { block } = bem('hook-form-date-input');

function DateInput(props) {
  const {
    className,
    defaultValue,
    disabled,
    label,
    name,
    optional,
    modifiers,
    timeZone,
    onChange,
    useDefaultLabel = false,
  } = props;

  const intl = useIntl();
  const {
    control,

    formState: { errors },
  } = useFormContext();

  const validators = makeValidators(props, intl);
  const hasError = Object.keys(errors).includes(name);

  const handleOnChange = onChange || (date => (timeZone ? zonedTimeToUtc(date, timeZone) : date).toISOString());
  const todayInTimeZone = timeZone ? adjustDateForTimezone(new Date(), timeZone) : new Date();

  return (
    <div className={block(className)}>
      <Label name={name} message={label} optional={optional} />
      <Controller
        rules={validators}
        control={control}
        name={name}
        defaultValue={defaultValue}
        // eslint-disable-next-line no-shadow
        render={({ field: { onChange, value } }) => {
          return (
            <DateControls
              setDate={date => {
                onChange(handleOnChange(date));
              }}
              date={value}
              disabled={disabled}
              hasError={hasError}
              isControl
              useDefaultLabel={useDefaultLabel}
              modifiers={{
                ...modifiers,
                today: todayInTimeZone,
              }}
              timeZone={timeZone}
            />
          );
        }}
      />
      <ErrorMessage name={name} errors={errors} />
    </div>
  );
}

DateInput.propTypes = {
  className: PropTypes.string,
  defaultValue: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  disabled: PropTypes.bool,
  label: intlMessage,
  modifiers: PropTypes.shape({
    disabled: PropTypes.oneOfType([
      PropTypes.func,
      PropTypes.shape({
        before: PropTypes.instanceOf(Date),
        after: PropTypes.instanceOf(Date),
      }),
    ]),
  }),
  name: PropTypes.string,
  optional: PropTypes.bool,
  timeZone: PropTypes.string,
  onChange: PropTypes.func,
  useDefaultLabel: PropTypes.bool,
};

export default DateInput;
